import type { UnknownAction } from '@reduxjs/toolkit'
import { nanoid } from '@reduxjs/toolkit'
import type { Observable } from 'rxjs'
import { catchError, concat, defer, EMPTY, filter, fromEvent, map, merge, mergeMap, of, withLatestFrom } from 'rxjs'
import type { Epic } from '../../index.js'
import type { ISpinStatusEntry } from '../../Slice/Spin/SpinStatusSlice.ts'
import { SPIN_STATE_REVALIDATE_AFTER_MS, spinStatusSlice } from '../../Slice/Spin/SpinStatusSlice.ts'
import { spinMetaSlice } from '../../Slice/Spin/SpinMetaSlice.ts'
import { authenticationExpiredOr } from '../../../Framework/Epic/AuthenticationExpired.js'
// Imported directly rather than through the injected repositories: SpinMeta is Promise-based and not in the
// rxjs-ajax Repository barrel. Tests mock the module.
import { fetchSpinState } from '../../../Repository/Visualization/SpinMeta.js'

const {
  requestSpinState,
  revalidateWatchedSpinStates,
  spinDataChanged,
  requestSpinStateStarted,
  requestSpinStateSuccess,
  requestSpinStateFailure
} = spinStatusSlice.actions

/** Whether a request for the scope goes out: never while one is in flight, and a recent answer is kept unless forced. */
const shouldRequest = (entry: ISpinStatusEntry | undefined, force: boolean, now: number): boolean => {
  if (entry?.status === 'loading') {
    return false
  }
  if (entry?.status !== 'ready' || force) {
    return true
  }
  return entry.validatedAt === undefined || now - entry.validatedAt >= SPIN_STATE_REVALIDATE_AFTER_MS
}

const fetchState = (scopeId: string, entry: ISpinStatusEntry | undefined, failedRequest: string): Observable<UnknownAction> => {
  const requestId = nanoid()
  // Without the data a 304 would leave nothing to keep, so only a cached answer is revalidated.
  const etag = entry?.data ? entry.etag : undefined
  return concat(
    of(requestSpinStateStarted({ scopeId, requestId })),
    defer(() => fetchSpinState(scopeId, etag)).pipe(
      mergeMap((result) => {
        const validatedAt = Date.now()
        if (result.kind === 'unchanged') {
          return of(requestSpinStateSuccess({ scopeId, requestId, validatedAt }))
        }
        const success = requestSpinStateSuccess({ scopeId, requestId, validatedAt, fresh: { data: result.state, etag: result.etag } })
        // The As-of unclustered counts move with the state; a first answer has nothing cached to drop.
        const changed = entry?.data !== undefined && (result.etag === null || result.etag !== entry.etag)
        return changed ? of(success, spinMetaSlice.actions.invalidateSpinMeta({ scopeIds: [scopeId] })) : of(success)
      }),
      catchError((error: { status?: number }) =>
        authenticationExpiredOr({
          error,
          failedRequest,
          failureAction: of(requestSpinStateFailure({ scopeId, requestId }))
        })
      )
    )
  )
}

/**
 * Fetches a scope's state, or revalidates the cached one with `If-None-Match` (a 304 keeps it). One request per scope
 * at a time — `Started` is emitted synchronously, so a second request already sees `loading` — and none while the
 * session is expired. `spinDataChanged` forces a revalidation.
 */
export const fetchSpinStateEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(isAnyStateRequest),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      if (state.authentication.sessionExpired) {
        return EMPTY
      }
      const now = Date.now()
      const targets = requestSpinState.match(action)
        ? [{ scopeId: action.payload.scopeId, force: action.payload.force ?? false }]
        : spinDataChanged.match(action)
          ? [{ scopeId: action.payload.scopeId, force: true }]
          : Object.entries(state.spinStatus.byScope)
              .filter(([, entry]) => entry.watchers > 0)
              .map(([scopeId]) => ({ scopeId, force: false }))
      const requests = targets
        .filter(({ scopeId, force }) => shouldRequest(state.spinStatus.byScope[scopeId], force, now))
        .map(({ scopeId }) => fetchState(scopeId, state.spinStatus.byScope[scopeId], action.type))
      return merge(...requests)
    })
  )

function isAnyStateRequest(
  action: UnknownAction
): action is ReturnType<typeof requestSpinState | typeof spinDataChanged | typeof revalidateWatchedSpinStates> {
  return requestSpinState.match(action) || spinDataChanged.match(action) || revalidateWatchedSpinStates.match(action)
}

/** The window coming back to the front: focus, or the tab becoming visible again. */
export const windowRegained = (): Observable<unknown> =>
  typeof window === 'undefined'
    ? EMPTY
    : merge(fromEvent(window, 'focus'), fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible')))

/** Revalidates the watched scopes when the user returns to the tab, where the nightly clustering may have landed. */
export const makeSpinFocusRevalidationEpic =
  (regained$: Observable<unknown>): Epic =>
  () =>
    regained$.pipe(map(() => revalidateWatchedSpinStates()))

export const spinFocusRevalidationEpic: Epic = (action$, state$, deps) =>
  makeSpinFocusRevalidationEpic(windowRegained())(action$, state$, deps)
